import { useEffect, useRef } from 'react'
import { createChart } from '@/features/summary-statistics/charts/chartFactory.js'
import { cardDescription, formatCardValue, resolveCardColor } from '@/features/summary-statistics/cardDisplay'
import type { StatCard } from '@/features/summary-statistics/cards'

/**
 * One card's breakdown, for the cards that do not open an analysis dialog of
 * their own (Phase 1I) — Total Facilities, Service Level, Spare Capacity, Open
 * DCs, Demand Fulfilled, Total Customers, Holding Cost.
 *
 * Replaces the `v-dialog` inside SummaryStatisticsBlocks.vue. What that dialog
 * got from Vuetify and this has to provide itself: a focus trap is out of scope,
 * but Escape closes, the backdrop closes, focus moves into the dialog on open
 * and returns to what opened it, and the title is `aria-labelledby`-linked.
 */

export type ChartData = {
  utilizationPct: number
  serviceLevelPct: number
  demandFulfilledPct: number
  effectiveFacilities: unknown[]
}

export function StatCardDetail({
  card,
  chartData,
  onClose,
}: {
  card: StatCard
  chartData: ChartData
  onClose: () => void
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const closeRef = useRef<HTMLButtonElement | null>(null)
  const color = resolveCardColor(card.color)

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      // Back to the card that opened this, so keyboard users do not land at the
      // top of the document.
      opener?.focus?.()
    }
  }, [onClose])

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext?.('2d')
    if (!ctx) return
    // The factory is framework-neutral and registers its own Chart.js pieces.
    const chart = createChart(ctx, card, chartData)
    // Destroyed on close AND on card change: Chart.js refuses to bind a second
    // chart to a canvas that still has one.
    return () => chart?.destroy?.()
  }, [card, chartData])

  return (
    <div
      className="stat-detail-backdrop"
      data-testid="stat-card-detail-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="stat-detail-title"
        data-testid="stat-card-detail"
        className="stat-detail-card"
      >
        {/* A resolved colour, not the Vuetify name: `background: primary` is a
            declaration the browser drops, which left white text on white. */}
        <div className="stat-detail-title" style={{ backgroundColor: color }}>
          <i className={`mdi ${card.icon}`} aria-hidden="true" />
          <span id="stat-detail-title" data-testid="stat-card-detail-title">{card.label}</span>
        </div>

        <div className="stat-detail-body">
          <div className="stat-detail-summary">
            <div className="stat-detail-value" style={{ color }}>{formatCardValue(card)}</div>
            {card.subtitle && <div className="stat-detail-subtitle">{card.subtitle}</div>}
            <hr className="stat-detail-divider" />
            <div className="stat-detail-description">{cardDescription(card.id)}</div>
          </div>

          <div className="chart-container">
            <canvas ref={canvasRef} />
          </div>
        </div>

        <div className="stat-detail-actions">
          <button
            type="button"
            ref={closeRef}
            className="stat-detail-close"
            data-testid="stat-card-detail-close"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
